import { useState, useEffect } from 'react';

// ── Types ─────────────────────────────────────────────────────────────────────
export type Tenant = { id: string; name: string; slug: string; plan_code?: string; };
export type TenantUser = {
  id: string;
  tenant_id: string;
  user_id: string;
  role: string;
  created_at: string;
  tenant_name?: string;
  is_super_admin?: boolean;
};

interface Props {
  user: TenantUser | null;
  isNew: boolean;
  tenants: Tenant[];
  onClose: () => void;
  onSave: (data: { user_id: string; tenant_id: string; role: string }) => void;
}

// ── Constants ─────────────────────────────────────────────────────────────────
const ROLES = ['owner', 'admin', 'editor', 'member', 'viewer'] as const;
const ROLE_META: Record<string, { icon: string; desc: string; color: string }> = {
  owner:  { icon: 'ri-vip-crown-line',    desc: 'Acceso completo + puede invitar usuarios', color: 'text-amber-700 bg-amber-50 border-amber-200' },
  admin:  { icon: 'ri-user-settings-line', desc: 'Acceso completo a todos los módulos',      color: 'text-blue-700 bg-blue-50 border-blue-200' },
  editor: { icon: 'ri-edit-2-line',        desc: 'Puede editar contenido del tenant',         color: 'text-emerald-700 bg-emerald-50 border-emerald-200' },
  member: { icon: 'ri-user-line',          desc: 'Acceso estándar a módulos asignados',       color: 'text-slate-700 bg-slate-50 border-slate-200' },
  viewer: { icon: 'ri-eye-line',           desc: 'Solo lectura — sin permisos de edición',    color: 'text-slate-400 bg-slate-50 border-slate-100' },
};

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const PLAN_BADGE: Record<string, string> = {
  free:       'bg-slate-100 text-slate-500',
  pro:        'bg-amber-50 text-amber-700',
  enterprise: 'bg-emerald-50 text-emerald-700',
};

// ── Component ─────────────────────────────────────────────────────────────────
export default function AssignUserModal({ user, isNew, tenants, onClose, onSave }: Props) {
  const [userId, setUserId]     = useState('');
  const [tenantId, setTenantId] = useState('');
  const [role, setRole]         = useState<string>('owner');
  const [error, setError]       = useState('');

  useEffect(() => {
    if (!isNew && user) {
      setUserId(user.user_id);
      setTenantId(user.tenant_id);
      setRole(user.role);
    } else {
      setUserId('');
      setTenantId(tenants[0]?.id ?? '');
      setRole('owner');
    }
    setError('');
  }, [user, isNew, tenants]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isNew && !UUID_REGEX.test(userId.trim())) {
      setError('El User ID debe ser un UUID válido (ej. de Supabase Auth)');
      return;
    }
    if (!tenantId) { setError('Selecciona un tenant'); return; }
    onSave({ user_id: userId.trim(), tenant_id: tenantId, role });
  };

  const rm = ROLE_META[role] ?? ROLE_META.member;

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white border border-slate-200 rounded-2xl w-full max-w-md overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-blue-50 text-blue-600">
              <i className={`${isNew ? 'ri-user-add-line' : 'ri-user-settings-line'} text-sm`}></i>
            </span>
            <div>
              <h2 className="text-slate-900 font-semibold text-sm">
                {isNew ? 'Asignar usuario a tenant' : 'Editar asignación'}
              </h2>
              {!isNew && user && (
                <p className="text-slate-400 text-xs font-mono truncate max-w-[200px]">
                  {user.user_id.slice(0, 12)}…{user.user_id.slice(-6)}
                </p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-7 h-7 flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-all cursor-pointer"
          >
            <i className="ri-close-line text-base"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">

          {/* User ID (new only) */}
          {isNew && (
            <div>
              <label className="text-slate-600 text-xs font-medium mb-1.5 block">
                User ID <span className="text-slate-400 font-normal">(UUID de Supabase Auth)</span>
              </label>
              <input
                value={userId}
                onChange={(e) => { setUserId(e.target.value); setError(''); }}
                placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-slate-900 font-mono placeholder-slate-400 outline-none focus:border-blue-400 transition-colors"
              />
              <div className="flex items-start gap-1.5 mt-1.5">
                <span className="w-3.5 h-3.5 flex items-center justify-center text-blue-400 shrink-0 mt-0.5">
                  <i className="ri-information-line text-xs"></i>
                </span>
                <p className="text-slate-400 text-xs leading-relaxed">
                  Encuéntralo en <strong className="text-slate-500">Supabase → Authentication → Users</strong>.
                  El usuario debe existir en Auth antes de asignarlo.
                </p>
              </div>
            </div>
          )}

          {/* Tenant */}
          <div>
            <label className="text-slate-600 text-xs font-medium mb-1.5 block">Tenant</label>
            {isNew ? (
              <select
                value={tenantId}
                onChange={(e) => { setTenantId(e.target.value); setError(''); }}
                required
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-blue-400 transition-colors cursor-pointer"
              >
                <option value="">Seleccionar tenant…</option>
                {tenants.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name} ({t.slug}){t.plan_code ? ` — ${t.plan_code}` : ''}
                  </option>
                ))}
              </select>
            ) : (
              <div className="px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg flex items-center justify-between">
                <p className="text-slate-700 text-sm font-medium">{user?.tenant_name}</p>
                {(() => {
                  const t = tenants.find((x) => x.id === user?.tenant_id);
                  return t?.plan_code ? (
                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${PLAN_BADGE[t.plan_code] ?? PLAN_BADGE.free}`}>
                      {t.plan_code}
                    </span>
                  ) : null;
                })()}
              </div>
            )}
          </div>

          {/* Role */}
          <div>
            <label className="text-slate-600 text-xs font-medium mb-2 block">Rol</label>
            <div className="grid grid-cols-1 gap-2">
              {ROLES.map((r) => {
                const meta = ROLE_META[r];
                return (
                  <button
                    key={r}
                    type="button"
                    onClick={() => setRole(r)}
                    className={`flex items-center gap-3 p-3 rounded-xl border text-left transition-all cursor-pointer ${
                      role === r
                        ? `${meta.color} ring-1 ring-inset ring-current`
                        : 'bg-white border-slate-200 text-slate-400 hover:border-slate-300 hover:text-slate-700'
                    }`}
                  >
                    <span className="w-6 h-6 flex items-center justify-center shrink-0">
                      <i className={`${meta.icon} text-sm`}></i>
                    </span>
                    <div className="flex-1">
                      <p className="text-xs font-semibold capitalize">{r}</p>
                      <p className="text-[10px] leading-tight opacity-70 mt-0.5">{meta.desc}</p>
                    </div>
                    {role === r && (
                      <span className="w-4 h-4 flex items-center justify-center shrink-0">
                        <i className="ri-check-line text-xs"></i>
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Role hint */}
            <div className="mt-2 flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-50 border border-slate-200">
              <span className="w-4 h-4 flex items-center justify-center text-slate-400 shrink-0">
                <i className="ri-information-line text-xs"></i>
              </span>
              <p className="text-slate-400 text-xs">{rm.desc}</p>
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="flex items-center gap-2 px-3 py-2 bg-red-50 border border-red-200 rounded-lg">
              <i className="ri-error-warning-line text-red-500 text-sm shrink-0"></i>
              <p className="text-red-600 text-xs">{error}</p>
            </div>
          )}

          {/* Buttons */}
          <div className="flex justify-end gap-3 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-slate-500 hover:text-slate-900 border border-slate-200 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap"
            >
              {isNew ? 'Asignar usuario' : 'Guardar cambios'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
